import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { useNotification } from '../../context/NotificationContext';
import { formatImageUrl } from '../../utils/imageUtils';

const BlueTickManagement = () => {
    const [requests, setRequests] = useState([]);
    const [price, setPrice] = useState(0);
    const [loading, setLoading] = useState(true);
    const { showSuccess, showError } = useNotification();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersRes, priceRes] = await Promise.all([
                adminAPI.getPendingBlueTickUsers(), // I need to add this to API utils
                adminAPI.getBlueTickPrice()
            ]);

            if (usersRes.data.success) setRequests(usersRes.data.data);
            if (priceRes.data.success) setPrice(priceRes.data.data);
        } catch (error) {
            showError("Failed to fetch blue tick data");
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (userId) => {
        try {
            const res = await adminAPI.approveBlueTick(userId);
            if (res.data.success) {
                showSuccess("Blue tick approved");
                fetchData();
            }
        } catch (error) {
            showError("Approval failed");
        }
    };

    const handleUpdatePrice = async () => {
        try {
            const res = await adminAPI.setBlueTickPrice(price);
            if (res.data.success) {
                showSuccess("Price updated successfully");
            }
        } catch (error) {
            showError("Price update failed");
        }
    };

    if (loading) return <div className="p-8 text-center text-gold">Loading...</div>;

    return (
        <div className="p-6 space-y-8">
            <div className="flex justify-between items-center bg-gray-800 p-6 rounded-2xl shadow-xl">
                <div>
                    <h1 className="text-2xl font-bold text-gold">Blue Tick Management</h1>
                    <p className="text-gray-400 text-sm">Manage doctor certification requests and pricing</p>
                </div>
                <div className="flex items-center gap-4 bg-black/40 p-4 rounded-xl border border-gold/20">
                    <div>
                        <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Current Price (₹)</label>
                        <input
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className="bg-transparent text-xl font-bold text-white w-24 outline-none focus:text-gold transition-colors"
                        />
                    </div>
                    <button
                        onClick={handleUpdatePrice}
                        className="bg-gold text-dark px-4 py-2 rounded-lg font-bold hover:opacity-90 transition-opacity"
                    >
                        Update
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {requests.length === 0 ? (
                    <div className="col-span-full py-20 text-center bg-gray-800/50 rounded-2xl border border-dashed border-gray-700">
                        <p className="text-gray-500">No pending blue tick requests</p>
                    </div>
                ) : (
                    requests.map(user => (
                        <div key={user._id} className="bg-gray-800 rounded-2xl overflow-hidden hover:border-gold/50 transition-colors group">
                            <div className="h-32 bg-gradient-to-br from-gold/20 to-transparent"></div>
                            <div className="px-6 pb-6 -mt-12 text-center">
                                <div className="w-24 h-24 mx-auto rounded-full border-4 border-gray-800 p-1 bg-gray-700 relative mb-4">
                                    <img
                                        src={formatImageUrl(user.profile_image) || `https://ui-avatars.com/api/?name=${user.name}`}
                                        alt={user.name}
                                        className="w-full h-full rounded-full object-cover"
                                    />
                                    {user.is_blue_tick && (
                                        <div className="absolute -bottom-1 -right-1 bg-blue-500 p-1.5 rounded-full border-2 border-gray-800 shadow-lg">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293l-4 4a1 1 0 01-1.414 0l-2-2a1 1 0 111.414-1.414L9 10.586l3.293-3.293a1 1 0 111.414 1.414z" /></svg>
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-1">{user.name}</h3>
                                <p className="text-gold text-xs font-semibold uppercase tracking-wider mb-4">{user.specialization || 'General Doctor'}</p>
                                <div className="bg-black/30 p-3 rounded-xl mb-6 text-left space-y-1">
                                    <p className="text-[10px] text-gray-500 uppercase font-bold">License Number</p>
                                    <p className="text-white text-sm font-mono">{user.license_number || 'N/A'}</p>
                                </div>
                                <button
                                    onClick={() => handleApprove(user._id)}
                                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    Approve Blue Tick
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default BlueTickManagement;
